import { ContentState } from "draft-js";
import { List } from "immutable";
import { Observable, WritableObservable } from "micro-observables";
import { Category, SubCategory } from "@domain/lms/category/category";
import { Author } from "@domain/lms/content";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { ContentLevel } from "@domain/lms/contentLevel";
import { ChapterDraft, CourseDraft, CourseStatus } from "@domain/lms/course/courseDraft";
import { CourseSection, LoadingSection } from "@domain/lms/course/courseSection";
import { ContentVisibility } from "@domain/lms/visibility";
import { Media } from "@domain/media/media";
import { withId } from "@utils/domain";
import { ChapterController } from "./chapterController";

export class CourseEditorController {
	private previousValues: CourseDraft[];
	private readonly currentValueIndex = new WritableObservable(0);

	readonly course: Observable<CourseDraft>;

	constructor(initialCourseDraft: CourseDraft) {
		this.previousValues = [initialCourseDraft];
		this.course = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	setTitle(title: string): void {
		this.updateCourse({ title });
	}
	setDescription(description: ContentState): void {
		this.updateCourse({ description });
	}
	setBanner(banner?: Media): void {
		this.updateCourse({ banner });
	}
	setIntroduction(introduction?: Media): void {
		this.updateCourse({ introduction });
	}
	setDuration(duration: number): void {
		this.updateCourse({ duration });
	}
	setCategory(category?: Category): void {
		this.updateCourse({ category });
	}
	setSubCategory(subCategory?: SubCategory): void {
		this.updateCourse({ subCategory });
	}
	setStatus(status: CourseStatus): void {
		this.updateCourse({ status });
	}
	setLanguage(language: ContentLanguage): void {
		this.updateCourse({ language });
	}
	setLevel(level?: ContentLevel): void {
		this.updateCourse({ level });
	}
	setPublisher(publisher?: Author): void {
		this.updateCourse({ publisher });
	}
	setOwnership(keepOwnership?: boolean): void {
		this.updateCourse({ keepOwnership });
	}
	setVisibility(visibility: ContentVisibility): void {
		this.updateCourse({ visibility });
	}
	toggleWorkspace(id: string): void {
		const course = this.course.get();
		const workspaceIds = course.workspaceIds.update((ids) => (ids.has(id) ? ids.remove(id) : ids.add(id)));
		this.updateCourse({ workspaceIds });
	}

	isValidForDraft(): boolean {
		const course = this.course.get();
		return course.title.length > 0;
	}

	isValidForNextStep(): boolean {
		const course = this.course.get();
		const firstChapter = course.content.chapters.first();
		if (!firstChapter) {
			return false;
		}

		const loadingSections = course.content.chapters.flatMap<CourseSection | LoadingSection>(
			(c: ChapterDraft) => c.sections
		);
		return (
			course.title.length > 0 &&
			firstChapter.title.length > 0 &&
			!firstChapter.sections.isEmpty() &&
			(course.visibility === "PUBLIC" || course.workspaceIds.size > 0) &&
			!loadingSections.find((s) => s.type === "LOADING")
		);
	}

	isValidForPublication(): boolean {
		const course = this.course.get();
		return (
			this.isValidForNextStep() &&
			course.description.hasText() &&
			course.category != null &&
			course.duration > 0 &&
			!!course.level
		);
	}

	addChapter(): void {
		this.updateCourse({
			content: {
				...this.course.get().content,
				chapters: this.course.get().content.chapters.push(
					withId({
						title: "",
						sections: List(),
						exercises: List(),
					})
				),
			},
		});
	}

	chapterController(chapter: ChapterDraft): ChapterController {
		return new ChapterController(
			(updater) => this.updateChapter(chapter.id, updater),
			() => this.removeChapter(chapter.id)
		);
	}

	updateCourse(courseUpdate: Partial<CourseDraft>): void {
		const course: CourseDraft = { ...this.course.get(), ...courseUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), course];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	moveSection(fromChapterId: string, toChapterId: string, fromIndex: number, toIndex: number): void {
		const chapters = this.course.get().content.chapters;
		const fromChapterIndex = chapters.findIndex((c) => c.id === fromChapterId);
		const toChapterIndex = chapters.findIndex((c) => c.id === toChapterId);
		const fromChapter = chapters.get(fromChapterIndex);
		const toChapter = chapters.get(toChapterIndex);
		const movedSection = fromChapter?.sections.get(fromIndex);
		if (!movedSection || !toChapter) {
			return;
		}
		this.updateChapters(
			chapters
				.update(fromChapterIndex, (chapter) => ({ ...chapter, sections: chapter.sections.remove(fromIndex) }))
				.update(toChapterIndex, (chapter) => ({ ...chapter, sections: chapter.sections.insert(toIndex, movedSection) }))
		);
	}

	private updateChapters(chapters: List<ChapterDraft>): void {
		const currentContent = this.course.get().content;
		this.updateCourse({ content: { ...currentContent, chapters } });
	}

	private updateAssets(assets: List<Media>): void {
		const currentContent = this.course.get().content;
		this.updateCourse({ content: { ...currentContent, assets } });
	}

	addAsset(media: Media): void {
		this.updateCourse({
			content: {
				...this.course.get().content,
				assets: this.course.get().content.assets.push(media),
			},
		});
	}

	private updateChapter(id: string, update: (current: ChapterDraft) => ChapterDraft): void {
		this.updateChapters(this.course.get().content.chapters.map((c) => (c.id === id ? update(c) : c)));
	}

	private removeChapter(id: string): void {
		const course = this.course.get();
		const index = course.content.chapters.findIndex((s) => s.id === id);
		if (index < 0) {
			return;
		}
		this.updateChapters(this.course.get().content.chapters.remove(index));
	}

	removeAssetById(id: string): void {
		const course = this.course.get();
		const index = course.content.assets.findIndex((s) => s.id === id);
		if (index < 0) {
			return;
		}
		this.updateAssets(this.course.get().content.assets.remove(index));
	}
}
